/* eslint-disable @typescript-eslint/no-use-before-define */

import { useCallback } from 'react';
import { useQuery } from '@apollo/client';

import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import SuspenseQuery from 'components/SuspenseQuery';

import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import { MandateModuleDataDocument } from './graphql/queries/mandateModuleData.gql';
import getMandateDocument from './libs/getMandateDocument';
import machineConfig, { model, State } from './machine';
import MandateCheck from './pages/MandateCheck';
import MandateReview from './pages/MandateReview';
import MandateUpload from './pages/MandateUpload';

type MandateUploadProps = {
  companyProfileId: string;
};

const Mandate = asBentoModule<MandateUploadProps>(
  ({ companyProfileId, onDone }) => {
    const MandateQuery = useQuery(MandateModuleDataDocument, {
      variables: { companyProfileId },
    });

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={MandateQuery}
      >
        {(data) => (
          <MandateComponent
            moduleData={{
              data,
            }}
            onDone={onDone}
          />
        )}
      </SuspenseQuery>
    );
  },
);

const MandateComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    const onPrev = useCallback(() => {
      send(model.events.PREV());
    }, [send]);

    const { company, companyUser } = moduleData.data.viewer;

    if (companyUser.isMandatee !== true) {
      throw new Error('The Mandate module should be accessed by a mandatee');
    }

    const mandateDocument = getMandateDocument(company);
    const stringifiedState = stringifyState<State>(state);

    switch (stringifiedState) {
      case State.UPLOAD:
        return (
          <MandateUpload
            companyProfile={company.profile}
            onPrev={onPrev}
            onUploadComplete={(documentFile) =>
              send(model.events.SUBMIT_DOCUMENT_FILE(documentFile))
            }
          />
        );

      case State.CHECK:
      case State.SEND:
        return (
          <MandateCheck
            documentUrl={state.context.documentFile?.url ?? ''}
            isSending={state.matches(State.SEND)}
            onPrev={onPrev}
            onValidate={() => send(model.events.VALIDATE_DOCUMENT())}
          />
        );

      case State.REVIEW:
      case State.DELETE: {
        return (
          <MandateReview
            companyProfile={company.profile}
            isDeleting={state.matches(State.DELETE)}
            mandateDocument={mandateDocument}
            onContinue={() => send(model.events.NEXT())}
            onDelete={(kybDocumentId) =>
              send(model.events.DELETE_DOCUMENT({ kybDocumentId }))
            }
            onPrev={onPrev}
          />
        );
      }

      case State.INIT:
      case State.DONE:
      case State.ABORT:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },
  {
    devTools: true,
    machineConfig,
  },
);

export default Mandate;
